import React, { useEffect, useRef, useState } from 'react'
import { useNotify, useTranslate } from 'react-admin'
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import LoadingButton from '@mui/lab/LoadingButton'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { itemSupplierService } from 'src/services/reservation/itemSupplier/itemSupplier.service'
import { supplierServices } from 'src/services/reservation/supplier/supplier.service'
import { currencyServices } from 'src/services/reservation/currency/currency.service'

export const EditItemSupplierModal = ({ item, open, setOpen, onComplete }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const [amountTotal, setAmountTotal] = useState(item?.amount_total ?? 0)
  const [amountPending, setAmountPending] = useState(item?.amount_pending ?? 0)
  const [dueDate, setDueDate] = useState(item?.due_date ?? '')
  const [status, setStatus] = useState(item?.status ?? '')
  const [suppliers, setSuppliers] = useState([])
  const [defaultSupplier, setDefaultSupplier] = useState(null)
  const [supplierOpen, setSupplierOpen] = useState(false)
  const [supplierLoading, setSupplierLoading] = useState(false)
  const [supplierId, setSupplierId] = useState(item?.supplier_id ?? '')
  const [loading, setLoading] = useState(false)
  const [currencyList, setCurrencyList] = useState([])
  const [loadingCurrencyList, setLoadingCurrencyList] = useState(false)
  const [currencyId, setCurrencyId] = useState(item?.currency_id ?? '')
  const [code, setCode] = useState(item?.code ?? '')
  const statusOptions = [
    'PAID',
    'UNPAID',
    'PARTIALLY_PAID',
    'NOT_REQUIRED',
    'AVAILABLE',
    'APPLIED',
    'PARTIALLY_APPLIED',
    'DELETED',
  ]
  const searchTimeoutSuppliersRef = useRef()

  useEffect(() => {
    if (open) {
      clearTimeout(searchTimeoutSuppliersRef.current)
      setAmountTotal(item?.amount_total ?? 0)
      setAmountPending(item?.amount_pending ?? 0)
      setDueDate(item?.due_date ?? '')
      setStatus(item?.status ?? '')
      setSupplierOpen(false)
      setSuppliers([])
      setCurrencyId(item?.currency_id ?? '')
      setCode(item?.code ?? '')
      getCurrencies()
      if (item?.supplier_id) {
        setSupplierId(item.supplier_id)
        if (item?.supplier_name) {
          setDefaultSupplier({ id: item.supplier_id, name: item.supplier_name })
        } else {
          setDefaultSupplier(null)
          supplierServices
            .getByParam({ id: item.supplier_id })
            .then((results) => {
              const found = Array.isArray(results) ? results.find((s) => Number(s.id) === Number(item.supplier_id)) : null
              if (found) setDefaultSupplier({ id: found.id, name: found.name })
            })
            .catch(() => {})
        }
      } else {
        setSupplierId('')
        setDefaultSupplier(null)
      }
    }
  }, [open, item])

  const getCurrencies = async () => {
    try {
      setLoadingCurrencyList(true)
      const allCurrencies = await currencyServices.getByAll()
      setCurrencyList(allCurrencies)
      setLoadingCurrencyList(false)
    } catch (error) {
      setCurrencyList([])
      setLoadingCurrencyList(false)
    }
  }

  const getSuppliers = (name) => {
    clearTimeout(searchTimeoutSuppliersRef.current)
    if (!name || name.length < 3) {
      setSuppliers([])
      setSupplierOpen(false)
      return
    }
    setSupplierOpen(false)
    searchTimeoutSuppliersRef.current = setTimeout(async () => {
      try {
        setSupplierLoading(true)
        const results = await supplierServices.getByParam(
          {
            name: name,
            operator: 'ilike',
            limit: 99
          }
        )
        setSuppliers(Array.isArray(results) ? results : [])
        setSupplierOpen(true)
      } catch (error) {
        setSuppliers([])
        setSupplierOpen(false)
      } finally {
        setSupplierLoading(false)
      }
    }, 300)
  }

  const selectedSupplier =
    suppliers.find((s) => Number(s.id) === Number(supplierId)) ||
    (supplierId ? defaultSupplier : null)

  const onSubmit = async () => {
    if (loading) return
    setLoading(true)
    try {
      await itemSupplierService.updateItemSupplier(item.id, {
        ...item,
        amount_total: Number(amountTotal),
        amount_pending: Number(amountPending),
        due_date: dueDate,
        status,
        supplier_id: supplierId,
        currency_id: currencyId,
        code,
      })
      notify('ra.notification.edit_item_supplier_success', { type: 'success' })
      onComplete?.()
      setOpen(false)
    } catch (error) {
      notify('ra.notification.edit_item_supplier_error', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      fullWidth
      maxWidth='sm'
      fullScreen={fullScreen}
      open={open}
      onClose={() => !loading && setOpen(false)}
    >
      <DialogTitle>{translate('ra.title.edit_item_supplier')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={3} style={{ marginTop: 0 }}>
          <EditInputsWrap label={translate('ra.field.total')} required md={6}>
            <TextField
              type='number'
              step='any'
              label=''
              fullWidth
              size='small'
              value={amountTotal}
              onChange={(e) => setAmountTotal(e.target.value)}
              InputProps={{
                startAdornment: (
                  <span style={{ marginRight: 8 }}>
                    {item?.currency_relation?.short_name ?? ''}
                  </span>
                ),
              }}
            />
          </EditInputsWrap>
          <EditInputsWrap label={translate('ra.field.amount_pending')} required md={6}>
            <TextField
              type='number'
              step='any'
              label=''
              fullWidth
              size='small'
              value={amountPending}
              onChange={(e) => setAmountPending(e.target.value)}
              InputProps={{
                startAdornment: (
                  <span style={{ marginRight: 8 }}>
                    {item?.currency_relation?.short_name ?? ''}
                  </span>
                ),
              }}
            />
          </EditInputsWrap>
                    <EditInputsWrap label={translate('ra.field.currency')} md={12}>
            {loadingCurrencyList ? (
              <Skeleton variant='rectangular' width={'100%'} height={40} />
            ) : (
              <Select
                fullWidth
                size='small'
                value={currencyId}
                onChange={(e) => setCurrencyId(e.target.value)}
              >
                {currencyList.map((currency) => (
                  <MenuItem key={currency.id} value={currency.id}>
                    {`${currency.symbol} ${currency.long_name} (${currency.short_name})`}
                  </MenuItem>
                ))}
              </Select>
            )}
          </EditInputsWrap>
          <EditInputsWrap label={translate('ra.field.due_date')} md={12}>
            <TextField
              type='date'
              fullWidth
              size='small'
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </EditInputsWrap>
          <EditInputsWrap label={translate('ra.field.status')} md={12}>
            <Select
              fullWidth
              size='small'
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {statusOptions.map((option) => (
                <MenuItem value={option} key={option}>
                  {translate(`ra.status.${option}`)}
                </MenuItem>
              ))}
            </Select>
          </EditInputsWrap>
          <EditInputsWrap label={translate('ra.field.supplier')} required md={12}>
            <Autocomplete
              open={supplierOpen}
              onClose={() => setSupplierOpen(false)}
              options={suppliers}
              getOptionLabel={(option) => option?.name ?? ''}
              isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
              loading={supplierLoading}
              filterOptions={(options) => options}
              clearable
              value={selectedSupplier}
              onChange={(e, newValue) => {
                setSupplierId(newValue?.id ?? null)
                if (!newValue) {
                  setSuppliers([])
                  setSupplierOpen(false)
                }
              }}
              onInputChange={(e, newInputValue, reason) => {
                if (reason === 'input') {
                  if (!selectedSupplier) getSuppliers(newInputValue)
                } else if (reason === 'clear') {
                  setSupplierId('')
                  setSuppliers([])
                  setSupplierOpen(false)
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size='small'
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {supplierLoading ? <CircularProgress color='inherit' size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </EditInputsWrap>

          <EditInputsWrap label={translate('ra.field.code')} md={12}>
            <TextField
              fullWidth
              size='small'
              value={code}
              inputProps={{ maxLength: 255 }}
              onChange={(e) => setCode(e.target.value)}
            />
          </EditInputsWrap>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          variant='outlined'
          color='error'
          onClick={() => setOpen(false)}
          disabled={loading}
        >
          {translate('ra.action.cancel')}
        </Button>
        <LoadingButton variant='contained' startIcon={<SaveIcon />} onClick={onSubmit} loading={loading}>
          {translate('ra.action.save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
